import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { trackPageView } from "./utils/posthog";
import { flushPendingKitConversion } from "./utils/kit-conversion";
import { appRoutes, NotFoundRoute } from "./routes";
import { PROGRAM_DOCUMENTS } from "./utils/ai30ProgramRouting.mjs";

const queryClient = new QueryClient();

// Component to track page views
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView();

    // Kit redirects immediately after a confirmed subscription. Flush any
    // queued, non-PII conversion after the destination page and PostHog load.
    const flushResult = flushPendingKitConversion(location.pathname);
    let retryTimer: number | undefined;
    let retryAttempts = 0;

    if (flushResult === "waiting_for_posthog") {
      retryTimer = window.setInterval(() => {
        retryAttempts += 1;
        const result = flushPendingKitConversion(location.pathname);
        if (result !== "waiting_for_posthog" || retryAttempts >= 100) {
          window.clearInterval(retryTimer);
        }
      }, 100);
    }

    // Note: UTM parameters are already captured in index.html posthog initialization
    // We don't need to set them again here to avoid duplication
    return () => window.clearInterval(retryTimer);
  }, [location]);

  return null;
};

// Keep route context and load standalone documents when React hands off to them.
const LocationRedirect = ({ to, preserveLocation }: { to: string; preserveLocation?: boolean }) => {
  const { search, hash } = useLocation();
  const target = preserveLocation ? `${to}${search}${hash}` : to;
  // Standalone documents must load their HTML when reached through React links.
  const programDocument = Object.prototype.hasOwnProperty.call(PROGRAM_DOCUMENTS, to);
  const standaloneDocument = programDocument || appRoutes.some((route) => route.kind === 'document' && route.path === to);
  useEffect(() => {
    if (!standaloneDocument) return;
    // A React Link has already changed the address. Replacing that same URL
    // only navigates within the document, so explicitly reload its static HTML.
    if (window.location.pathname === to) window.location.reload();
    else window.location.replace(target);
  }, [standaloneDocument, target, to]);
  return standaloneDocument
    ? <a href={target}>{programDocument ? 'Open your AI 30 program' : 'Open presentation'}</a>
    : <Navigate to={target} replace />;
};

// Providers + route table, WITHOUT a router. Shared by the client entry (wrapped
// in BrowserRouter, below) and the SSG prerender entry (wrapped in StaticRouter in
// src/entry-server.tsx), so both render the exact same tree.
//
// The <Route> list is derived from the single route source in src/routes.tsx, so
// the client, the prerender, and the sitemap all agree on what each path renders.
export const AppRoutes = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <PageViewTracker />
      <Routes>
        {appRoutes.map((route) => {
          if (route.kind === 'document') {
            return <Route key={route.path} path={route.path} element={<LocationRedirect to={route.path} preserveLocation />} />;
          }
          if (route.kind === 'redirect') {
            return (
              <Route
                key={route.path}
                path={route.path}
                element={<LocationRedirect to={route.to} preserveLocation={route.preserveLocation} />}
              />
            );
          }
          const { Component } = route;
          return <Route key={route.path} path={route.path} element={<Component />} />;
        })}
        {/* ADD ALL CUSTOM ROUTES IN src/routes.tsx, ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFoundRoute />} />
      </Routes>
    </TooltipProvider>
  </QueryClientProvider>
);

// Client app: HelmetProvider + BrowserRouter around the shared route table.
const App = () => (
  <HelmetProvider>
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  </HelmetProvider>
);

export default App;
